import { SdkFunctionWrapper } from '@OnArk/sdk';
import { retryTransient } from '@onark/shared/retryTransient';

/**
 * The server-side SDK wrapper: retry a GraphQL *query* when the origin was
 * never reached, and leave a breadcrumb when that happens.
 *
 * Wired once at `getSdk(client, retryingSdkWrapper)` in withAuth, which covers
 * every generated operation without touching a single call site.
 *
 * WHY QUERIES ONLY. `SdkFunctionWrapper` sees the operation type, and mutations
 * are not idempotent — retrying `createChurch` after a timeout risks a second
 * church, and a timeout is exactly the case where we cannot tell whether the
 * first one landed. Reads are safe to repeat; writes buy their safety with a
 * server-side idempotency key, which is a different change than this one.
 *
 * See `@onark/shared/retryTransient` for what counts as transient, and brain
 * #382 for the Cloudflare DNS fault this exists to absorb.
 */

/**
 * A short, safe description of the failure — a status or a socket code, never
 * the body.
 *
 * The body is the whole problem: Cloudflare's 530 response is a full HTML error
 * page, and logging it verbatim is what turned these incidents into unreadable
 * megabyte payloads in Rollbar. The status is the entire diagnostic value.
 */
function describeFailure(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const { response } = error as { response?: { status?: unknown } };

    if (typeof response?.status === 'number') return `http_${response.status}`;

    const { code } = error as { code?: unknown };

    if (typeof code === 'string') return code;

    const cause = (error as { cause?: { code?: unknown } }).cause;

    if (typeof cause?.code === 'string') return cause.code;
  }

  return 'unknown';
}

/**
 * Report a retried operation to PostHog Logs.
 *
 * Deliberately fired ONCE per operation rather than once per attempt:
 * `serverLog` awaits an OTLP flush (the Pages Router freezes the function right
 * after the response, so it has to), and paying that inside the retry loop
 * would add flush latency to a request that is already limping.
 *
 * Legacy-router note: when `/admin/*` moves to the App Router this belongs in
 * the `@onark/observability` catalog as a proper domain event. `serverLog` is
 * the established path for a `pages/` SSR breadcrumb today — it is what
 * `pages/_error.tsx` already uses — and adding a catalog publisher for the
 * legacy runtime is a bigger change than this fix deserves.
 */
async function reportRetries(
  operationName: string,
  outcome: 'recovered' | 'exhausted',
  reasons: string[]
): Promise<void> {
  const { serverLog } = await import('@OnArk/utils/serverLog');

  await serverLog(
    outcome === 'recovered' ? 'warn' : 'error',
    `GraphQL query ${operationName} hit a transient upstream failure`,
    {
      attempts: reasons.length + 1,
      operationName,
      outcome,
      reasons: reasons.join(','),
    }
  );
}

export const retryingSdkWrapper: SdkFunctionWrapper = async (
  action,
  operationName,
  operationType
) => {
  if (operationType !== 'query') return action();

  const reasons: string[] = [];

  try {
    const result = await retryTransient(() => action(), {
      onRetry: ({ error }) => reasons.push(describeFailure(error)),
    });

    // Only interesting when it actually had to retry — the happy path stays
    // silent, and free.
    if (reasons.length > 0) {
      await reportRetries(operationName, 'recovered', reasons);
    }

    return result;
  } catch (error) {
    if (reasons.length > 0) {
      await reportRetries(operationName, 'exhausted', reasons);
    }

    throw error;
  }
};
